import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Shield, Users, Clock, ChevronRight } from 'lucide-react';

export function ProductDetail() {
  const { id } = useParams();

  // This would typically come from an API or database
  const products = {
    'cookie-consent-manager': {
      name: 'Cookie Consent Manager',
      category: 'consent',
      description: 'Comprehensive cookie consent and preference management solution',
      image: 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?auto=format&fit=crop&q=80&w=1200',
      price: 'Starting at $499/mo',
      longDescription: `Our Cookie Consent Manager provides a comprehensive solution for managing cookie consent and user preferences across your digital properties. Built with privacy regulations in mind, it helps you stay compliant while maintaining a great user experience.`,
      features: [
        'Customizable consent banner and preference center',
        'Automatic cookie scanning and categorization',
        'Multi-language support',
        'Real-time consent logging',
        'Integration with major analytics platforms',
        'Detailed compliance reporting'
      ],
      benefits: [
        'Ensure compliance with GDPR, CCPA, and other privacy regulations',
        'Improve user trust with transparent data collection practices',
        'Reduce legal risks with proper consent management',
        'Streamline consent collection across multiple domains'
      ],
      specs: {
        'Deployment': 'Cloud-based / On-premise',
        'Updates': 'Automatic monthly updates',
        'Support': '24/7 enterprise support',
        'Integration': 'REST API / JavaScript SDK',
        'Data Storage': 'Regional data centers',
        'Compliance': 'GDPR, CCPA, LGPD, PIPEDA'
      }
    },
    'privacy-rights-portal': {
      name: 'Privacy Rights Portal',
      category: 'privacy',
      description: 'Streamline privacy rights requests and compliance',
      image: 'https://images.unsplash.com/photo-1560732488-6b0df240254a?auto=format&fit=crop&q=80&w=1200',
      price: 'Starting at $399/mo',
      longDescription: `The Privacy Rights Portal simplifies the management of data subject access requests (DSARs) and privacy rights. Automate request handling, verify identities, and maintain compliance with privacy regulations.`,
      features: [
        'Automated request intake and processing',
        'Identity verification system',
        'Customizable workflow automation',
        'Secure document delivery',
        'Request status tracking',
        'Compliance documentation'
      ],
      benefits: [
        'Reduce manual processing time by up to 70%',
        'Ensure timely response to privacy requests',
        'Maintain detailed audit trails',
        'Improve data subject satisfaction'
      ],
      specs: {
        'Deployment': 'Cloud-based',
        'Updates': 'Bi-weekly updates',
        'Support': 'Business hours support',
        'Integration': 'REST API',
        'Data Storage': 'Regional data centers',
        'Compliance': 'GDPR, CCPA, CPRA'
      }
    }
  };

  const product = products[id];

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center transition-all duration-300">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Product Not Found</h2>
          <Link to="/products" className="text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-300">
            Return to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-all duration-300">
      {/* Navigation */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Link 
              to="/products" 
              className="text-gray-600 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] flex items-center transition-colors duration-300"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Back to Products
            </Link>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Link 
                to="/products" 
                className="hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] transition-colors duration-300"
              >
                Products
              </Link>
              <ChevronRight className="h-4 w-4 mx-1" />
              <span className="text-gray-900 dark:text-white">{product.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-6">
                {product.name}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                {product.longDescription}
              </p>
              <div className="space-y-6">
                <div className="text-2xl font-bold text-[#5e9bb8] dark:text-[#5e9bb8]">
                  {product.price}
                </div>
                <button className="px-6 py-3 bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] text-white rounded-xl font-medium transform hover:-translate-y-0.5 transition-all duration-300">
                  Request Demo
                </button>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#5e9bb8]/10 to-[#499da8]/10 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <img
                src={product.image}
                alt={product.name}
                className="rounded-2xl shadow-xl w-full transform group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-8">
            Key Features
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-start group">
                <div className="flex-shrink-0">
                  <Check className="h-6 w-6 text-[#5e9bb8] transform group-hover:scale-110 transition-transform duration-300" />
                </div>
                <p className="ml-3 text-lg text-gray-700 dark:text-gray-300">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm py-16 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-8">
            Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {product.benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 backdrop-blur-sm p-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <p className="text-lg text-gray-700 dark:text-gray-300">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technical Specifications */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-8">
            Technical Specifications
          </h2>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden transition-all duration-300">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {Object.entries(product.specs).map(([key, value]) => (
                <div key={key} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-300">
                  <div className="grid grid-cols-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{key}</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{value}</dd>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#5e9bb8] to-[#499da8] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-xl text-[#5e9bb8]-100 mb-8">
            Contact our team to schedule a demo and learn more about {product.name}
          </p>
          <div className="flex justify-center space-x-4">
            <button className="px-6 py-3 bg-white text-[#5e9bb8] rounded-xl hover:bg-gray-50 transform hover:-translate-y-0.5 transition-all duration-300">
              Schedule Demo
            </button>
            <button className="px-6 py-3 bg-[#4d8299] text-white rounded-xl hover:bg-[#3d8289] transform hover:-translate-y-0.5 transition-all duration-300">
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}